/**
 * =============================================================================
 * InstanceKpisStrip — Faixa de KPIs da aba "Instâncias" segmentada por canal.
 *
 * Pools independentes (WhatsApp, WhatsApp Oficial e Instagram) + status
 * transversais (Pendente, Restritas = Suspensas + Canceladas) + bloco de
 * auditoria com o detalhamento por provider (contratado / em uso / disponível).
 *
 * Visual alinhado ao padrão dos KPI cards da aba Inboxes:
 *   - Fundo pastel sólido (sem borda visível, sem header colorido).
 *   - Sombra none com hover:shadow-md, cantos 2xl.
 *   - Ícone colorido + título escuro + número grande à direita.
 *   - Barra de progresso interna mantém a cor do canal.
 *
 * CHANGELOG:
 *   v1.2.0 - 2026-08-19 - Separação do pool WhatsApp em "WhatsApp" e
 *            "WhatsApp Oficial" + novo bloco recolhível "Detalhamento por
 *            provider" (auditoria de contratado x em uso x disponível),
 *            alimentado pela RPC get_tenant_instance_quota_by_provider.
 *   v1.1.0 - 2026-06-29 - Refatoração visual: removido header colorido e
 *            border outline. Adotado padrão de fundo pastel sólido (igual
 *            MetricCard da aba Inboxes). Mantida a barra de progresso e a
 *            segmentação Disponível / Em Uso.
 *   v1.0.0 - 2026-06-29 - Versão inicial (header colorido + border).
 * =============================================================================
 */
import { useMemo, useState } from 'react';
import {
  Instagram,
  MessageCircle,
  BadgeCheck,
  AlertTriangle,
  Clock,
  XCircle,
  Crown,
  ChevronDown,
  ListChecks,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { InlineNotice } from '@/components/ui/inline-notice';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import type { ProviderQuotaRow } from '@/hooks/useTenantInstanceQuotaByProvider';

/**
 * Métricas agregadas de um canal (WhatsApp, WhatsApp Oficial ou Instagram).
 *
 * v1.2.1: os valores vêm da RPC de cota por provider (fonte única de verdade),
 * portanto `available` significa "vagas contratáveis/livres" (não apenas
 * instâncias já criadas em status `released`) e `inUse` já considera a vaga do
 * plano compartilhada entre providers do mesmo canal.
 */
export interface ChannelPoolMetrics {
  /** Limite efetivo (plano base + add-ons) para o canal. */
  limit: number;
  /** Vagas livres do canal (cota contratada ainda não consumida). */
  available: number;
  /** Vagas consumidas do canal (instâncias ativas, incluindo a vaga do plano). */
  inUse: number;
}

interface InstanceKpisStripProps {
  whatsapp: ChannelPoolMetrics;
  /** Pool do canal WhatsApp Oficial (WABA). Opcional para compatibilidade. */
  whatsappOfficial?: ChannelPoolMetrics;
  instagram: ChannelPoolMetrics;
  /** Instâncias aguardando pagamento (transversal aos canais). */
  pending: number;
  /** Instâncias com pagamento atrasado (transversal). */
  suspended: number;
  /** Instâncias canceladas / inativas (transversal). */
  cancelled: number;
  /** Linhas de cota por provider (auditoria). */
  providerQuotas?: ProviderQuotaRow[];
  className?: string;
}

/**
 * Conjunto de tokens visuais por canal — fundo pastel sólido + acento.
 * `barFill` mantém a cor do canal para reforçar identidade visual,
 * mas é trocada para amber/rose conforme o nível de uso.
 */
interface ChannelTone {
  cardBg: string;
  iconColor: string;
  labelColor: string;
  barTrack: string;
  barFill: string;
}

/** Rótulo amigável de um channel_type. */
function channelLabel(channelType: string): string {
  switch ((channelType || '').toLowerCase()) {
    case 'whatsapp':
      return 'WhatsApp';
    case 'whatsapp_oficial':
      return 'WhatsApp Oficial';
    case 'instagram':
      return 'Instagram';
    default:
      return channelType || '—';
  }
}

/**
 * Card de um canal — layout horizontal compacto com ícone à esquerda,
 * título, números à direita e barra de progresso fina abaixo.
 *
 * Quando `limit === 0`, vira CTA de "Contratar canal".
 */
function ChannelPoolCard({
  label,
  Icon,
  metrics,
  tone,
  spanClass,
}: {
  label: string;
  Icon: React.ElementType;
  metrics: ChannelPoolMetrics;
  tone: ChannelTone;
  spanClass: string;
}) {
  const navigate = useNavigate();
  const { limit, available, inUse } = metrics;
  const pct = limit > 0 ? Math.min(100, Math.round((inUse / limit) * 100)) : 0;

  // Cor da barra: padrão (canal), amber ≥80%, rose =100%.
  let fillClass = tone.barFill;
  if (limit > 0 && pct >= 100) fillClass = 'bg-rose-500';
  else if (limit > 0 && pct >= 80) fillClass = 'bg-amber-500';

  const notContracted = limit === 0;

  return (
    <Card
      className={cn(
        'border-0 shadow-none transition-all hover:shadow-md',
        tone.cardBg,
        spanClass,
      )}
    >
      <CardContent className="p-3 space-y-2">
        {/* Linha do título: ícone + label + contador principal */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Icon className={cn('h-5 w-5 shrink-0', tone.iconColor)} />
            <p className="text-xs font-medium truncate text-gray-900 dark:text-gray-100">
              {label}
            </p>
          </div>
          <p className="text-xl font-bold leading-tight shrink-0 text-gray-900 dark:text-gray-100">
            {notContracted ? '0/0' : `${inUse}/${limit}`}
          </p>
        </div>

        {/* Barra de progresso fina (uso vs limite) */}
        <div className={cn('w-full rounded-full h-1.5', tone.barTrack)}>
          <div
            className={cn(
              'h-1.5 rounded-full transition-all',
              notContracted ? 'bg-muted-foreground/30' : fillClass,
            )}
            style={{ width: `${notContracted ? 0 : pct}%` }}
          />
        </div>

        {/* Linha inferior: Disponível / Em Uso ou CTA */}
        {notContracted ? (
          <Button
            variant="link"
            size="sm"
            className={cn('h-auto p-0 text-[10px]', tone.labelColor)}
            onClick={() => navigate('/tenant/plans')}
          >
            <Crown className="h-3 w-3 mr-1" /> Contratar canal
          </Button>
        ) : (
          <div className="flex items-center justify-between text-[10px] text-gray-700 dark:text-gray-300">
            <span>
              <strong className="text-gray-900 dark:text-gray-100">{available}</strong> Disponível
            </span>
            <span>
              <strong className="text-gray-900 dark:text-gray-100">{inUse}</strong> Em Uso
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Card pequeno transversal (Pendente / Restritas) — mesmo DNA visual
 * dos cards de canal, em formato ainda mais compacto.
 */
function StatusMiniCard({
  label,
  Icon,
  value,
  caption,
  cardBg,
  iconColor,
  secondary,
}: {
  label: string;
  Icon: React.ElementType;
  value: number | string;
  caption: string;
  cardBg: string;
  iconColor: string;
  /** Texto opcional ao lado do número principal (ex.: "0 / 0"). */
  secondary?: { primary: number; secondary: number };
}) {
  return (
    <Card className={cn('flex-1 min-w-0 border-0 shadow-none transition-all hover:shadow-md', cardBg)}>
      <CardContent className="p-3 flex flex-col justify-between h-full">
        <div className="flex items-center gap-2 min-w-0">
          <Icon className={cn('h-4 w-4 shrink-0', iconColor)} />
          <p className="text-[11px] font-medium truncate text-gray-900 dark:text-gray-100">
            {label}
          </p>
        </div>
        {secondary ? (
          <div className="flex items-baseline gap-1 mt-1">
            <span className="text-xl font-bold leading-tight text-gray-900 dark:text-gray-100">
              {secondary.primary}
            </span>
            <span className="text-[10px] text-gray-500">/</span>
            <span className="text-xl font-bold leading-tight text-gray-500">
              {secondary.secondary}
            </span>
          </div>
        ) : (
          <span className="text-xl font-bold leading-tight text-gray-900 dark:text-gray-100 mt-1">
            {value}
          </span>
        )}
        <span className="text-[10px] text-gray-600 dark:text-gray-400 leading-none truncate mt-0.5">
          {caption}
        </span>
      </CardContent>
    </Card>
  );
}

/**
 * Bloco recolhível de auditoria: uma linha por provider com o que foi
 * contratado, o que está em uso e o que resta disponível.
 */
function ProviderQuotaBreakdown({ rows }: { rows: ProviderQuotaRow[] }) {
  const [open, setOpen] = useState(false);

  // Detecta canais onde mais de um provider recebe a mesma cota do plano
  // (shares_plan_pool / is_default). Nesse caso a soma das linhas não é o total.
  const sharedChannels = useMemo(() => {
    const byChannel = new Map<string, number>();
    rows.forEach((r) => {
      if (r.allowed_qty !== 0) {
        byChannel.set(r.channel_type, (byChannel.get(r.channel_type) ?? 0) + 1);
      }
    });
    return [...byChannel.entries()].filter(([, count]) => count > 1).map(([ch]) => ch);
  }, [rows]);

  if (rows.length === 0) return null;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <Card className="border-0 shadow-none bg-muted/40">
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="w-full flex items-center justify-between gap-2 p-3 text-left"
          >
            <span className="flex items-center gap-2 text-xs font-medium text-gray-900 dark:text-gray-100">
              <ListChecks className="h-4 w-4 text-muted-foreground" />
              Detalhamento por provider
              <Badge variant="secondary" className="text-[10px]">
                {rows.length}
              </Badge>
            </span>
            <ChevronDown
              className={cn(
                'h-4 w-4 text-muted-foreground transition-transform',
                open && 'rotate-180',
              )}
            />
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent className="p-3 pt-0 space-y-3">
            {sharedChannels.length > 0 && (
              <InlineNotice variant="info">
                A vaga do plano é compartilhada entre os providers do mesmo canal
                ({sharedChannels.map(channelLabel).join(', ')}). Nesses casos o valor
                "Contratado" se repete por provider — o total do canal é o do card acima.
              </InlineNotice>
            )}

            <div className="overflow-x-auto">
              <table className="w-full min-w-[520px] text-xs">
                <thead>
                  <tr className="text-[10px] uppercase text-muted-foreground">
                    <th className="text-left font-medium py-1.5">Provider</th>
                    <th className="text-left font-medium py-1.5">Canal</th>
                    <th className="text-right font-medium py-1.5">Contratado</th>
                    <th className="text-right font-medium py-1.5">Em uso</th>
                    <th className="text-right font-medium py-1.5">Disponível</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const unlimited = row.allowed_qty < 0;
                    const pct =
                      !unlimited && row.allowed_qty > 0
                        ? Math.min(100, Math.round((row.in_use_qty / row.allowed_qty) * 100))
                        : 0;

                    return (
                      <tr key={row.provider_id} className="border-t border-border/50">
                        <td className="py-2 pr-2">
                          <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
                            {row.display_name || row.provider_name}
                          </div>
                          {/* Barra de uso do provider */}
                          <div className="mt-1 w-full max-w-[180px] rounded-full h-1 bg-muted">
                            <div
                              className={cn(
                                'h-1 rounded-full transition-all',
                                unlimited
                                  ? 'bg-muted-foreground/30'
                                  : pct >= 100
                                    ? 'bg-rose-500'
                                    : pct >= 80
                                      ? 'bg-amber-500'
                                      : 'bg-emerald-500',
                              )}
                              style={{ width: `${unlimited ? 0 : pct}%` }}
                            />
                          </div>
                        </td>
                        <td className="py-2 pr-2">
                          <Badge variant="outline" className="text-[10px]">
                            {channelLabel(row.channel_type)}
                          </Badge>
                        </td>
                        <td className="py-2 text-right font-semibold text-gray-900 dark:text-gray-100">
                          {unlimited ? 'Ilimitado' : row.allowed_qty}
                        </td>
                        <td className="py-2 text-right">{row.in_use_qty}</td>
                        <td className="py-2 text-right">
                          {unlimited ? '—' : row.available_qty}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}

/**
 * Faixa principal de KPIs. Grid 12 colunas no desktop; empilha em coluna
 * única no mobile preservando a ordem (WhatsApp, WhatsApp Oficial,
 * Instagram, Status) e, abaixo, o detalhamento por provider.
 */
export function InstanceKpisStrip({
  whatsapp,
  whatsappOfficial,
  instagram,
  pending,
  suspended,
  cancelled,
  providerQuotas = [],
  className,
}: InstanceKpisStripProps) {
  // Só exibe o card do canal oficial quando há métrica informada
  // (contratado > 0 ou instâncias existentes) — evita poluir tenants sem WABA.
  const showOfficial =
    !!whatsappOfficial &&
    (whatsappOfficial.limit > 0 ||
      whatsappOfficial.inUse > 0 ||
      whatsappOfficial.available > 0);

  return (
    <div className={cn('space-y-3', className)}>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
        {/* WhatsApp (não oficial) — verde-lima */}
        <ChannelPoolCard
          label="WhatsApp Lite"
          Icon={MessageCircle}
          metrics={whatsapp}
          spanClass={showOfficial ? 'lg:col-span-3' : 'lg:col-span-5'}
          tone={{
            cardBg: 'bg-lime-100 dark:bg-green-900/30',
            iconColor: 'text-green-700 dark:text-green-300',
            labelColor: 'text-green-700 dark:text-green-300',
            barTrack: 'bg-white/60 dark:bg-black/20',
            barFill: 'bg-emerald-500',
          }}
        />

        {/* WhatsApp Oficial (WABA) — esmeralda/azulado para diferenciar */}
        {showOfficial && (
          <ChannelPoolCard
            label="WhatsApp Oficial"
            Icon={BadgeCheck}
            metrics={whatsappOfficial!}
            spanClass="lg:col-span-3"
            tone={{
              cardBg: 'bg-teal-100 dark:bg-teal-900/30',
              iconColor: 'text-teal-700 dark:text-teal-300',
              labelColor: 'text-teal-700 dark:text-teal-300',
              barTrack: 'bg-white/60 dark:bg-black/20',
              barFill: 'bg-teal-500',
            }}
          />
        )}

        {/* Instagram Pool — rosa */}
        <ChannelPoolCard
          label="Instagram Pool"
          Icon={Instagram}
          metrics={instagram}
          spanClass={showOfficial ? 'lg:col-span-3' : 'lg:col-span-4'}
          tone={{
            cardBg: 'bg-pink-100 dark:bg-pink-900/30',
            iconColor: 'text-pink-700 dark:text-pink-300',
            labelColor: 'text-pink-700 dark:text-pink-300',
            barTrack: 'bg-white/60 dark:bg-black/20',
            barFill: 'bg-pink-500',
          }}
        />

        {/* Status transversais (Pendente + Restritas) */}
        <div className="lg:col-span-3 flex gap-2">
          <StatusMiniCard
            label="Pendente"
            Icon={Clock}
            value={pending}
            caption="Pagamento"
            cardBg="bg-amber-100 dark:bg-amber-900/30"
            iconColor="text-amber-700 dark:text-amber-300"
          />
          <StatusMiniCard
            label="Restritas"
            Icon={suspended > 0 ? AlertTriangle : XCircle}
            value={suspended + cancelled}
            caption="Susp. / Canc."
            cardBg="bg-rose-100 dark:bg-rose-900/30"
            iconColor="text-rose-700 dark:text-rose-300"
            secondary={{ primary: suspended, secondary: cancelled }}
          />
        </div>
      </div>

      {/* Auditoria: contratado x em uso x disponível por provider */}
      <ProviderQuotaBreakdown rows={providerQuotas} />
    </div>
  );
}
